import Export16 from '@carbon/icons-react/es/export/16';
import { useBoolean } from 'ahooks';
import { Tooltip } from 'antd';
import { cloneDeep, debounce, isEqual, keyBy } from 'lodash';
import { pipe } from 'ramda';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { ExportQueueStatus } from 'api/async-export';
import { ProjectId } from 'api/models';
import { Project, ProjectDetailTabKeys } from 'api/project';
import { Button } from 'components/atoms/button';
import type { Selectable } from 'components/atoms/grid';
import { IconWrapper } from 'components/atoms/icon-shell/icon-shell';
import { BreadCrumbs } from 'components/molecules/project-details/header/breadcrumbs';
import { ProjectTitle } from 'components/molecules/project-details/header/project-title';
import { ProjectDetailTabs } from 'components/molecules/project-details/header/tabs/tabs';
import { SidebarCollapseButton } from 'components/molecules/sidebar-collapse-button/SidebarCollapseButton';
import { toastService } from 'components/molecules/toast';
import { FilterMenuProject } from 'components/organisms/filter-menu-sidebar';
import {
  asColumnOrder,
  cleanUpColumnOrder,
  ColumnListPopover,
  ColumnOrder,
  GridChangeEvent,
} from 'components/organisms/grid-custom-column';
import { TaskGrid } from 'components/organisms/task-grid';
import { toColumnDef } from 'components/organisms/tasks/all-tasks-v2/column';
import {
  relativelySortByDefaultColumnOrder,
  relativelySortColumns,
} from 'components/organisms/tasks/all-tasks-v2/utils';
import { FilterContainer } from 'components/organisms/tasks/components';
import { exportAsCSV } from 'components/organisms/tasks/export';
import type {
  SingleSelectField,
  TextField,
} from 'components/organisms/tasks/fields';
import { TaskFilterView } from 'components/organisms/tasks/filter-views';
import { withOnUpdate } from 'components/organisms/tasks/task-list-v2/column';
import { useCreateTask } from 'components/organisms/tasks/use-create-task';
import { useFilters } from 'components/organisms/tasks/use-filters';
import {
  PartialValue,
  useUpdateTasks,
} from 'components/organisms/tasks/use-update-tasks';
import {
  default_column_order,
  readonly_fields,
} from 'components/organisms/tasks/utils';
import { ProjectTasksViewSwitcher } from 'components/pages/task-list/project-tasks-view-switcher';
import { useProjectTaskListFilter } from 'components/pages/task-list/use-project-task-list-filter';
import { Neon } from 'components/templates/neon';
import { TOGGLE_PROJECTS_SIDEBAR } from 'constant/constant';
import { RootState } from 'data';
import { useProjectPhaseByProjectId } from 'data/projects';
import { ProjectResource, useWithAccess } from 'hooks/rbac/useWithAccess';
import { useAccount } from 'hooks/use-account';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useCheckProjectBillability } from 'hooks/use-check-project-billablility';
import { useCustomerPreview } from 'hooks/use-customer-preview';
import { ClickEventTags, setMarkerForDataDog } from 'hooks/use-data-dog-logger';
import { useDistractionFreeMode } from 'hooks/use-distraction-free-mode';
import { useGrid } from 'hooks/use-grid';
import { useTranslate } from 'hooks/use-translation';
import { useIsExternalUser, useIsNativeUser } from 'hooks/use-user-type';
import { useProjectWebsocket } from 'hooks/websockets';
import { useQueryJiraConfig } from 'queries/jira-integration';
import { logger } from 'services';
import { FieldsStore, TaskField } from 'stores/fields';
import { withValidCustomTaskFields } from 'utils/duplicate-fields-transformer';
import { getRandomColor } from 'utils/get-random-color';
import { queueMacrotask } from 'utils/macrotask';
import { SelectionControls } from '../selection-controls';
import { useProjectTasksRefresh } from '../use-project-tasks-refresh';
import { useProjectTasksSocketUpdates } from '../use-project-tasks-socket-updates';
import { useSelections } from '../use-selections';

const excludedTaskFields = [
  'taskId',
  'taskDescription',
  'isCsatEnabled',
  'project',
  'privateTaskDescription',
];

const EMPTY_RECORD = {};
const excludedColumnsForReordering = ['checkbox', 'status'];

export const ProjectTaskListPage: FC<{
  project: Project;
}> = ({ project }) => {
  const { projectId } = project;
  const { accountId } = useAccount();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const { data: jiraIntegrationConfig } = useQueryJiraConfig();
  const phases = useProjectPhaseByProjectId(projectId);
  const { isPreview } = useCustomerPreview();
  const isExternalUser = useIsExternalUser();

  const excludedFields = useMemo(() => {
    // remove jiraIntegration from excluded fields ONLY when the integration is active
    if (jiraIntegrationConfig?.length === 0) {
      return [...excludedTaskFields, 'jiraIntegration'];
    }

    return excludedTaskFields;
  }, [jiraIntegrationConfig]);

  const excludedFieldsForColumns = useMemo(
    () => [
      ...excludedFields,
      'private',
      'completedLateBy',
      'phaseName',
      'taskDependencyStatus',
    ],
    [excludedFields],
  );

  const excludedFieldsForFilters = useMemo(
    () => [...excludedFields, 'phaseName'],
    [excludedFields],
  );

  const [fields, fieldsForFilter] = useMemo(() => {
    const taskFields = FieldsStore.enabledTaskFields(fieldsStore).map((field) =>
      withValidCustomTaskFields(accountId, cloneDeep(field)),
    );

    return [
      taskFields.filter(
        ({ fieldName }) => !excludedFieldsForColumns.includes(fieldName),
      ),
      taskFields
        .filter(
          ({ fieldName }) => !excludedFieldsForFilters.includes(fieldName),
        )
        .map((field) =>
          field.fieldName === 'projectPhaseId'
            ? ({
                ...field,
                metaData: {
                  ...field.metaData,
                  options: phases.map((phase) => ({
                    active: true,
                    label: phase?.projectPhaseName,
                    value: phase?.projectPhaseId,
                    color: getRandomColor(phase?.projectPhaseName || ''),
                  })),
                },
              } as TaskField)
            : field,
        ),
    ];
  }, [
    accountId,
    excludedFieldsForColumns,
    excludedFieldsForFilters,
    fieldsStore,
    phases,
  ]);

  const fieldsLookupByName = useMemo(
    () =>
      fieldsForFilter.length > 0
        ? (keyBy(fieldsForFilter, 'fieldName') as Record<string, TaskField>)
        : EMPTY_RECORD,
    [fieldsForFilter],
  );

  const fieldsLookupByNameForColumns = useMemo(
    () => keyBy(fields, 'fieldName') as Record<string, TaskField>,
    [fields],
  );

  const fields_loading = !FieldsStore.loaded(fieldsStore);

  const { filter, filterFunction, clearFilters, selectFilter, replaceFilter } =
    useFilters(fieldsLookupByName, fields_loading ? 'loading' : 'loaded');

  const [filterInitialized, setFilterInitialized] = useState(false);
  const projectField = FieldsStore.getTaskFieldByName('project')(
    fieldsStore,
  )[1] as (SingleSelectField & TaskField) | undefined;
  const taskNameField = FieldsStore.getTaskFieldByName('taskName')(
    fieldsStore,
  )[1] as (TextField & TaskField) | undefined;
  const selectableRef = useRef<Selectable>(null);
  const { selected, setSelected, count } = useSelections(selectableRef);
  const isCustomerPortal = isPreview || isExternalUser;

  useProjectTaskListFilter({
    localStorageKey: `task_list.query.project_id-${projectId}`,
    taskStatus: fields_loading ? 'loading' : 'loaded',
    replaceFilter,
    filter,
    fieldsLoading: fields_loading,
    fieldLookup: fieldsLookupByName,
    filterInitialized,
    setFilterInitialized,
  });

  const [
    isExportInProgress,
    { setTrue: setExportStarted, setFalse: setExportEnded },
  ] = useBoolean(false);

  const translate = useTranslate();

  useProjectTasksRefresh({
    projectId: ProjectId(projectId),
    fieldTransformer: useCallback(
      (field) => withValidCustomTaskFields(accountId, field) as TaskField,
      [accountId],
    ),
  });
  useProjectTasksSocketUpdates(
    ProjectId(projectId),
    setExportEnded,
    isExportInProgress,
  );
  useProjectWebsocket(ProjectId(projectId));

  const { gridApi, gridColumnApi, onGridReady } = useGrid();

  const isNativeUser = useIsNativeUser();

  const { hasAccessToResource } = useWithAccess();
  const projectPhaseAsReadOnlyIfNoAccess = useCallback(
    (readOnlyFields) =>
      project == null ||
      hasAccessToResource({
        rule: 'TASK_UPDATE_PHASE:WRITE',
        resourceData: ProjectResource(project),
      })
        ? readOnlyFields
        : readOnlyFields.concat(['projectPhaseId']),
    [hasAccessToResource, project],
  );
  const assigneeAsReadOnlyIfNoAccess = useCallback(
    (readOnlyFields) =>
      project == null ||
      hasAccessToResource({
        rule: 'TASK_ASSIGNEE:WRITE',
        resourceData: ProjectResource(project),
      })
        ? readOnlyFields
        : readOnlyFields.concat(['assignee']),
    [hasAccessToResource, project],
  );
  const updatedReadonlyFields = useMemo(
    () =>
      pipe(
        assigneeAsReadOnlyIfNoAccess,
        projectPhaseAsReadOnlyIfNoAccess,
      )(readonly_fields),

    [assigneeAsReadOnlyIfNoAccess, projectPhaseAsReadOnlyIfNoAccess],
  );

  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );
  const { checkIsProjectNonBillable } = useCheckProjectBillability();
  const { updateSingleTask } = useUpdateTasks();

  const createTask = useCreateTask();
  const onCreate = useCallback(
    (request: PartialValue[]) =>
      queueMacrotask(() => {
        if (projectField == null || taskNameField == null) {
          logger.error('field_missing', {
            error: new Error('project field or task name field missing'),
            projectField,
            taskNameField,
          });

          return;
        }
        createTask([
          { field: projectField, to: projectId },
          { field: taskNameField, to: request[0].to as string },
        ]);
      }),
    [createTask, projectField, projectId, taskNameField],
  );

  useEffect(() => {
    if (!gridApi || fields.length === 0) {
      return;
    }

    const toColumnDefs = pipe(
      toColumnDef({
        translate,
        isNativeUser,
        updatedReadonlyFields,
        isTimesheetFeatureEnabled,
        checkIsProjectNonBillable,
      }),
      relativelySortColumns,
      withOnUpdate(updateSingleTask, onCreate),
    );
    const columnDefs = toColumnDefs(fields);

    gridApi?.setColumnDefs(columnDefs);
  }, [
    checkIsProjectNonBillable,
    fields,
    gridApi,
    isNativeUser,
    isTimesheetFeatureEnabled,
    translate,
    updateSingleTask,
    updatedReadonlyFields,
    onCreate,
  ]);
  const defaultColumnState: ColumnOrder[] = useMemo(() => {
    const toColumnDefs = pipe(
      toColumnDef({
        translate,
        isNativeUser,
        updatedReadonlyFields,
        isTimesheetFeatureEnabled,
        checkIsProjectNonBillable,
      }),
      relativelySortByDefaultColumnOrder,
    );

    return asColumnOrder(
      toColumnDefs(fields).map((x) => ({ ...x, colId: x.field! })),
    );
  }, [
    checkIsProjectNonBillable,
    fields,
    isNativeUser,
    isTimesheetFeatureEnabled,
    translate,
    updatedReadonlyFields,
  ]);

  const [columnOrder, setColumnOrder] =
    useState<ColumnOrder[]>(defaultColumnState);

  const exportAsCsvWithToast = () => {
    setExportStarted();
    exportAsCSV({
      filter,
      projectId,
      columnOrder,
      lookup: fieldsLookupByNameForColumns,
      failExport: setExportEnded,
    })
      .then((exportStatus) => {
        if (exportStatus === ExportQueueStatus.Submitted) {
          toastService({
            title: translate('async.exports.in_progress.title'),
            message: translate('async.exports.in_progress.message'),
          }).success();
        }
      })
      .catch(() => {
        toastService({
          title: translate('async.exports.error.title'),
          message: translate('async.exports.error.message'),
        }).error();
      });
  };

  const handleColumnOrderChangedByView = useCallback(
    (changedColumnOrder: ColumnOrder[]) => {
      setColumnOrder(
        cleanUpColumnOrder(defaultColumnState)(changedColumnOrder),
      );
    },
    [defaultColumnState],
  );

  useEffect(() => {
    // Used to update the grid with latest columnOrder
    if (!gridColumnApi) {
      return;
    }
    gridColumnApi.applyColumnState({ state: columnOrder, applyOrder: true });
  }, [columnOrder, gridColumnApi]);

  const handleGridChange = useCallback(
    (event: GridChangeEvent) => {
      if (event) {
        const newColumnOrder = asColumnOrder(event.columnApi.getColumnState());

        if (!isEqual(newColumnOrder, columnOrder)) {
          setColumnOrder(newColumnOrder);
        }
      }
    },
    [columnOrder],
  );

  const throttledGridChange = useMemo(
    () => debounce(handleGridChange, 100),
    [handleGridChange],
  );

  const { active, exit, enter } = useDistractionFreeMode(
    TOGGLE_PROJECTS_SIDEBAR,
  );

  return (
    <Neon
      isDistractionFree={active}
      title={<ProjectTitle project={project} />}
      tabs={
        !isCustomerPortal ? (
          <ProjectDetailTabs
            projectId={ProjectId(projectId)}
            activeTabKey={ProjectDetailTabKeys.TaskList}
          />
        ) : null
      }
      sider={
        !isCustomerPortal ? (
          <FilterMenuProject
            fields={fields}
            lookup={fieldsLookupByName}
            filter={filter}
            selectFilter={selectFilter}
            clearFilters={clearFilters}
            isFieldsLoading={fields_loading}
            columnOrder={columnOrder}
            handleColumnOrderChange={handleColumnOrderChangedByView}
            defaultColumnOrder={defaultColumnState}
          />
        ) : null
      }
      breadcrumbs={<BreadCrumbs />}
      actions={
        count > 0
          ? {
              primary: (
                <SelectionControls
                  count={count}
                  reset={() => gridApi?.deselectAll()}
                  selectAll={() => gridApi?.selectAllFiltered()}
                />
              ),
            }
          : {
              primary: (
                <FilterContainer>
                  {!isCustomerPortal ? (
                    <SidebarCollapseButton
                      sidebarToggle={active ? exit : enter}
                    />
                  ) : null}
                  <TaskFilterView
                    isFieldsLoading={fields_loading}
                    filter={filter}
                    fields={fieldsForFilter}
                    selectFilter={(fil) => {
                      setMarkerForDataDog(
                        ClickEventTags.ProjectTaskListFilterChange,
                      );
                      selectFilter(fil);
                    }}
                    clearFilters={clearFilters}
                    lookup={fieldsLookupByName}
                    includeSavedViews={!!isCustomerPortal}
                    columnOrder={columnOrder}
                    defaultColumnOrder={defaultColumnState}
                    handleColumnOrderChange={handleColumnOrderChangedByView}
                  />
                </FilterContainer>
              ),
              secondary: (
                <>
                  <ColumnListPopover
                    columnOrder={columnOrder}
                    gridColumnApi={gridColumnApi ?? undefined}
                    excludedColumns={excludedColumnsForReordering}
                    defaultColumnOrder={default_column_order}
                  />
                  <Tooltip
                    title={<FormattedMessage id="export.tooltip" />}
                    placement="topLeft"
                  >
                    <Button
                      icon={
                        <IconWrapper>
                          <Export16 />
                        </IconWrapper>
                      }
                      type="text"
                      size="small"
                      loading={isExportInProgress}
                      onClick={exportAsCsvWithToast}
                      data-cy="project-task-list.export"
                    />
                  </Tooltip>
                </>
              ),
              central: isCustomerPortal ? (
                <ProjectTasksViewSwitcher projectId={ProjectId(projectId)} />
              ) : null,
            }
      }
      content={
        <TaskGrid
          projectId={projectId}
          filterFunction={filterFunction}
          initialized={filterInitialized}
          selected={selected}
          setSelected={setSelected}
          onGridReady={onGridReady}
          handleGridChange={throttledGridChange}
          gridApi={gridApi ?? undefined}
        />
      }
    />
  );
};
